import { useState, useEffect, useCallback } from 'react';
import { getTasks } from '@/utils/api';
import ManufacturingTab from '@/components/ManufacturingTab';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Factory } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const API = process.env.REACT_APP_BACKEND_URL;

const ManufacturingPage = () => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await getTasks();
      setTasks(res.data);
      setFilteredTasks(res.data);
    } catch (error) { if (error?.response?.status !== 401) toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!search) { setFilteredTasks(tasks); return; }
    const q = search.toLowerCase();
    setFilteredTasks(tasks.filter(t =>
      t.style_no?.toLowerCase().includes(q) ||
      t.client_name?.toLowerCase().includes(q) ||
      t.internal_cad_no?.toLowerCase().includes(q)
    ));
  }, [search, tasks]);

  const handleSelectTask = async (taskId) => {
    try {
      const res = await api.get(`${API}/api/tasks/${taskId}`);
      setSelectedTask(res.data);
    } catch (error) { if (error?.response?.status !== 401) toast.error('Failed to load task details'); }
  };

  const handleUpdate = async () => {
    if (selectedTask) await handleSelectTask(selectedTask.id);
  };

  return (
    <CollapsibleLayout>
      <div className="flex h-[calc(100vh-48px)]" data-testid="manufacturing-page">
        {/* Left: Task Selector */}
        <div className="w-72 border-r border-border bg-card flex flex-col flex-shrink-0">
          <div className="p-3 border-b border-border">
            <div className="flex items-center gap-2 mb-2">
              <Factory size={16} className="text-primary" />
              <h2 className="text-sm font-bold text-foreground">Manufacturing</h2>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search style no, client..."
                className="pl-8 h-7 text-xs"
                data-testid="mfg-page-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-xs text-muted-foreground text-center">Loading...</div>
            ) : filteredTasks.length === 0 ? (
              <div className="p-4 text-xs text-muted-foreground text-center">No tasks found</div>
            ) : (
              filteredTasks.map(t => (
                <button
                  key={t.id}
                  onClick={() => handleSelectTask(t.id)}
                  className={`w-full text-left px-3 py-2 text-xs border-b border-border/40 hover:bg-muted/50 transition-colors ${selectedTask?.id === t.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''}`}
                  data-testid={`mfg-task-${t.id}`}
                >
                  <div className="font-mono font-semibold text-foreground">{t.style_no || t.internal_cad_no || 'No Style'}</div>
                  <div className="text-[10px] text-muted-foreground">{t.client_name} | {t.piece_type}</div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: Manufacturing Details */}
        <div className="flex-1 overflow-y-auto">
          {selectedTask ? (
            <ManufacturingTab task={selectedTask} onUpdate={handleUpdate} />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center">
                <Factory size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">Select a task</p>
                <p className="text-xs">Choose a CAD from the left panel to view manufacturing details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </CollapsibleLayout>
  );
};

export default ManufacturingPage;
